import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../services/distributorService';
import useShippingRequest from '../hooks/useShippingRequest';
import { useLanguage } from '../i18n/LanguageContext';
import { decimalCents, parseCents, usd, validCents } from './distributor-cash/cashMoney';

const unwrap = (response) => response?.data?.data;
const MAX_THRESHOLD_CENTS = 100000000000;
const validPreferences = (row) =>
  row?.currency === 'USD' &&
  validCents(row.revision) &&
  validCents(row.thresholdCents) &&
  row.thresholdCents <= MAX_THRESHOLD_CENTS &&
  typeof row.inApp === 'boolean' &&
  typeof row.email === 'boolean';
const validAlerts = (value, distributorId) =>
  Array.isArray(value?.data) &&
  value.data.every(
    (row) =>
      typeof row?._id === 'string' &&
      row._id.length > 0 &&
      row.currency === 'USD' &&
      /^[a-f\d]{24}$/.test(row.distributorId) &&
      (!distributorId || row.distributorId === distributorId) &&
      validCents(row.remainingCents) &&
      validCents(row.thresholdCents) &&
      Number.isFinite(Date.parse(row.createdAt))
  );

export default function DistributorBankAlertsCredit({
  distributorId,
  readOnly = false,
  t: providedT,
}) {
  const { t: languageT, locale } = useLanguage();
  const t = providedT || languageT;
  const { begin, version } = useShippingRequest(`cash-alerts:${distributorId || 'own'}`);
  const locked = useRef(false);
  const [preferences, setPreferences] = useState(null);
  const [threshold, setThreshold] = useState('');
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const request = begin();
    locked.current = false;
    setBusy(false);
    setPreferences(null);
    setAlerts(null);
    setError('');
    setNotice('');
    Promise.all([
      distributorService.cashAlertPreferences(distributorId, { signal: request.signal }),
      distributorService.cashAlerts(distributorId, { signal: request.signal }),
    ])
      .then(([preferenceResponse, alertsResponse]) => {
        if (!request.isCurrent()) return;
        const pref = unwrap(preferenceResponse),
          rows = unwrap(alertsResponse);
        if (!validPreferences(pref) || !validAlerts(rows, distributorId))
          throw new Error('Invalid credit alerts');
        setPreferences(pref);
        setThreshold(decimalCents(pref.thresholdCents));
        setAlerts(rows.data);
      })
      .catch(() => {
        if (request.isCurrent()) setError('Bank alerts are unavailable. Please try again.');
      });
  }, [begin, version, distributorId, retry]);
  const save = async (event) => {
    event.preventDefault();
    if (readOnly || locked.current || !preferences) return;
    const thresholdCents = parseCents(threshold);
    if (thresholdCents === null)
      return setError('Enter a valid dollar threshold with up to two decimal places.');
    if (thresholdCents > MAX_THRESHOLD_CENTS)
      return setError('The low-credit threshold must not exceed $1,000,000,000.');
    locked.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const request = begin();
    const payload = {
      revision: preferences.revision,
      thresholdCents,
      inApp: preferences.inApp,
      email: preferences.email,
    };
    try {
      const row = unwrap(await distributorService.saveCashAlertPreferences(distributorId, payload));
      if (!request.isCurrent()) return;
      if (
        !validPreferences(row) ||
        row.revision <= payload.revision ||
        ['thresholdCents', 'inApp', 'email'].some((key) => row[key] !== payload[key])
      )
        throw new Error('Unconfirmed credit preferences');
      setPreferences(row);
      setThreshold(decimalCents(row.thresholdCents));
      setNotice('Bank alert preferences saved.');
    } catch {
      if (request.isCurrent()) {
        setPreferences(null);
        setAlerts(null);
        setError('Preferences could not be confirmed. Reload and try again.');
      }
    } finally {
      if (request.isCurrent()) {
        locked.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <fieldset className="me-cash me-cash-card me-cash-alerts">
      <legend>{t('Low-credit alerts')}</legend>
      <div className="me-cash-heading">
        <p>
          {t(
            'Receive an alert when Virtual Tissue Bank Balance reaches the shared dollar threshold. The default is $1,000.00.'
          )}
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (!locked.current) setRetry((value) => value + 1);
          }}
        >
          {t('Refresh')}
        </button>
      </div>
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {!preferences && !error && <p role="status">{t('Loading bank alerts...')}</p>}
      {preferences && (
        <form onSubmit={save}>
          <label>
            {t('Shared low-credit threshold (USD)')}
            <input
              inputMode="decimal"
              type="text"
              value={threshold}
              disabled={busy || readOnly}
              onChange={(event) => setThreshold(event.target.value)}
            />
          </label>
          <p>
            {t(
              'The dollar threshold applies to the distributor organization. Notification channels apply to this account.'
            )}
          </p>
          <label className="me-cash-authorize">
            <input
              type="checkbox"
              checked={preferences.inApp}
              disabled={busy || readOnly}
              onChange={(event) =>
                setPreferences((current) => ({ ...current, inApp: event.target.checked }))
              }
            />
            {t('In-app')}
          </label>
          <label className="me-cash-authorize">
            <input
              type="checkbox"
              checked={preferences.email}
              disabled={busy || readOnly}
              onChange={(event) =>
                setPreferences((current) => ({ ...current, email: event.target.checked }))
              }
            />
            {t('Email')}
          </label>
          {!readOnly && (
            <button type="submit" className="me-cash-primary" disabled={busy}>
              {t('Save alert preferences')}
            </button>
          )}
        </form>
      )}
      {alerts?.length === 0 && <p>{t('No low-credit alerts yet.')}</p>}
      {alerts?.length > 0 && (
        <ul className="me-dw-list">
          {alerts.map((row) => (
            <li key={row._id} className="py-3 text-sm">
              <strong>{t('{amount} credit remaining', { amount: usd(row.remainingCents) })}</strong>
              <p>
                {t('Alert threshold: {amount}', { amount: usd(row.thresholdCents) })} {'\u00b7'}{' '}
                {new Date(row.createdAt).toLocaleString(locale)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
